import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import * as Amplitude from 'utils/amplitude';

import * as EmployeeUtils from 'utils/employee';

import ProfileContext from 'contexts/ProfileContext';
import PretaxContext from 'contexts/PretaxContext';

import TopNavigationBar from 'components/common/TopNavigationBar';
import Footer from 'components/common/Footer';
import EmailBanner from 'components/common/EmailBanner';
import { ReactComponent as ExclamationMarkIcon } from '@twicapp/ui/icons/exclamation-mark.svg';

import InterstitialTaskModal from 'components/common/InterstitialTaskModal';
import Body2 from '@twicapp/ui/components/Body2';
import Title2 from '@twicapp/ui/components/Title2';

import styled from 'styled-components';

const Content = styled.main`
  background-color: ${({
    theme: {
      colors: {
        background: { primary },
      },
    },
  }) => primary};
  min-height: 100vh;
`;

const AccessAlert = styled(Body2)`
  display: block;
  height: 48px;
  background: #fff9cc;
  color: ${({
    theme: {
      colors: {
        text: { primary },
      },
    },
  }) => primary};
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  z-index: 1000;
  width: 100%;
  svg {
    height: 14.86px;
    width: 14.86px;
  }
`;

const AlertDescription = styled(Body2)`
  display: block;
`;

const AccessType = styled(Title2)`
  margin-left: 13px;
  margin-right: 4px;
`;

interface RootProps {
  paddingTop: string;
}

const Root = styled.div<RootProps>`
  position: relative;
  padding-top: ${({ paddingTop = '0px' }) => paddingTop};
`;

// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const isPrivacyPolicyVisible = (companyId: $TSFixMe) =>
  ['microsoft'].includes(companyId);

const BaseLayout = ({ children }: $TSFixMe) => {
  const { employee, company } = useContext(ProfileContext);
  const { id: companyId } = company;
  const { show_signin_banner } = employee;
  const { pretax_accounts } = useContext(PretaxContext);
  const location = useLocation();
  const [is_onboarding, setIsOnboarding] = useState(
    // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
    location.state ? location.state.onboarding : false
  );

  useEffect(() => {
    if (is_onboarding) {
      // @ts-expect-error ts-migrate(2551) FIXME: Property 'OnboardingInterstitialTaskViewed' does n... Remove this comment to see the full error message
      Amplitude.logEvent(Amplitude.EVENTS.OnboardingInterstitialTaskViewed, {});
    }
  }, [is_onboarding]);

  const is_usa = EmployeeUtils.getCountry({ employee }) === 'us';
  const has_pretax_accounts = EmployeeUtils.hasPretaxAccounts({
    employee,
    pretax_accounts,
  });
  const has_personal_email = employee && employee.personal_email;

  const onClose = () => {
    setIsOnboarding(false);
    Amplitude.logEvent(
      Amplitude.EVENTS.OnboardingInterstitialTasksDismissed,
      {}
    );
    window.history.replaceState({}, document.title);
  };

  return (
    <Elements stripe={stripePromise}>
      <Root paddingTop={show_signin_banner ? '48px' : '0'}>
        {show_signin_banner && (
          <AccessAlert>
            <ExclamationMarkIcon />
            <AccessType>Read Only Access Enabled: </AccessType>
            <AlertDescription>
              You are currently logged in as a member via the Twic Internal
              Portal
            </AlertDescription>
          </AccessAlert>
        )}
        <TopNavigationBar />
        {!has_personal_email && is_usa && has_pretax_accounts && (
          <EmailBanner first_name={employee.first_name} />
        )}
        <Content>
          {children}
          {is_onboarding && (
            <InterstitialTaskModal
              employee={employee}
              company={company}
              onClose={onClose}
              visible={is_onboarding}
            />
          )}
        </Content>
        <Footer showCompanyPrivacyPolicy={isPrivacyPolicyVisible(companyId)} />
      </Root>
    </Elements>
  );
};

export default BaseLayout;
